import { useState, type FormEvent } from 'react';
import { formatTaskKey,
  TASK_PRIORITY_LABEL,
  TASK_PRIORITIES,
  TASK_REPEAT_LABEL,
  TASK_REPEATS,
  type CompletionPolicy,
  type TaskPriority,
  type TaskRepeat,
  type TaskSummary,
  type UpdateTaskRequest,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { taskApi } from '../api/task.api';
import { useAuth } from '../hooks/useAuth';
import { DepartmentSelect } from './DepartmentSelect';
import { ProjectSelect } from './ProjectSelect';

/**
 * Editing a task after creation (creator or admin - the caller decides
 * whether to render this). Fields only: assignees, status, and the star
 * keep their own controls elsewhere in the panel.
 */

/** Same help lines the create form shows under "Completes when". */
const POLICY_HELP: Record<CompletionPolicy, string> = {
  ANY: 'Anyone assigned can mark it complete',
  ALL: 'Everyone assigned must complete their part',
  LEAD_ONLY: 'Only the lead can mark it complete',
};

/** ISO UTC -> the local wall-clock string <input type="datetime-local"> wants. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

/** Today as a local YYYY-MM-DD string (the shape <input type="date"> uses). */
function todayLocalDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function TaskEditForm({
  task, onSaved, onCancel,
}: {
  task: TaskSummary;
  onSaved: (task: TaskSummary) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [details, setDetails] = useState(task.details ?? '');
  const [priority, setPriority] = useState<TaskPriority>(task.priority);
  const { user } = useAuth();
  /** Only a Super Admin may change the due date; for everyone else the field
   *  is shown but disabled. The server enforces it either way. */
  const canChangeDue = user?.role.systemKey === 'SUPER_ADMIN';
  const originalDue = toLocalInput(task.dueAt).slice(0, 10);
  const [dueAt, setDueAt] = useState(originalDue);
  const [remindAt, setRemindAt] = useState(toLocalInput(task.remindAt));
  const [repeat, setRepeat] = useState<TaskRepeat>(task.repeat);
  const [departmentId, setDepartmentId] = useState<string | undefined>(task.departmentId ?? undefined);
  const [projectId, setProjectId] = useState<string | undefined>(task.projectId ?? undefined);
  const [policy, setPolicy] = useState<CompletionPolicy>(task.completionPolicy);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubtask = task.parentTaskId !== null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!title.trim()) { setError('Give the task a title'); return; }
    const dueChanged = canChangeDue && dueAt !== '' && dueAt !== originalDue;
    if (dueChanged && dueAt < todayLocalDate()) {
      setError("Due date can't be in the past");
      return;
    }

    const changes: UpdateTaskRequest = {
      title: title.trim(),
      details: details.trim() || null,
      priority,
      remindAt: remindAt ? new Date(remindAt).toISOString() : null,
      repeat,
      departmentId: departmentId ?? null,
      projectId: projectId ?? null,
      completionPolicy: policy,
      // Stored as the END of that local day, exactly as the create form does.
      ...(dueChanged ? { dueAt: new Date(`${dueAt}T23:59:59`).toISOString() } : {}),
    };

    setSaving(true);
    try {
      onSaved(await taskApi.update(task.id, changes));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the changes');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Edit {formatTaskKey(task.taskKey)}</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          Assignees and status are changed from the task view, not here.
        </p>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">
          {error}
        </p>
      )}

      <div>
        <label htmlFor="edit-title" className="block text-sm font-medium text-slate-700">Title</label>
        <input id="edit-title" value={title} maxLength={500}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
      </div>

      <div>
        <label htmlFor="edit-details" className="block text-sm font-medium text-slate-700">
          Details <span className="ml-1 text-slate-400">(optional)</span>
        </label>
        <textarea id="edit-details" value={details} rows={4} maxLength={5000}
          onChange={(e) => setDetails(e.target.value)}
          className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="block text-sm font-medium text-slate-700">Priority</span>
          <div className="mt-1.5 flex gap-1.5">
            {TASK_PRIORITIES.map((p) => (
              <button key={p} type="button" onClick={() => setPriority(p)}
                className={[
                  'rounded-lg px-3 py-2 text-sm font-medium ring-1 transition',
                  priority === p
                    ? 'bg-slate-900 text-white ring-slate-900'
                    : 'bg-white text-slate-600 ring-slate-300 hover:bg-slate-50',
                ].join(' ')}>
                {TASK_PRIORITY_LABEL[p]}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="block text-sm font-medium text-slate-700">Due</span>
          <input
            type="date"
            value={dueAt}
            min={todayLocalDate()}
            disabled={!canChangeDue}
            onChange={(e) => setDueAt(e.target.value)}
            aria-label="Due date"
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
          />
          <p className="mt-1 text-xs text-slate-500">
            {isSubtask && !originalDue ? 'Inherits the parent task. ' : ''}
            Only a Super Admin can change the due date
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="edit-remind" className="block text-sm font-medium text-slate-700">
            Remind on <span className="ml-1 text-slate-400">(optional)</span>
          </label>
          <input id="edit-remind" type="datetime-local" value={remindAt}
            onChange={(e) => setRemindAt(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
        </div>
        <div>
          <label htmlFor="edit-repeat" className="block text-sm font-medium text-slate-700">Repeat</label>
          <select id="edit-repeat" value={repeat}
            onChange={(e) => setRepeat(e.target.value as TaskRepeat)}
            className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100">
            {TASK_REPEATS.map((r) => (
              <option key={r} value={r}>{TASK_REPEAT_LABEL[r]}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <DepartmentSelect value={departmentId} onChange={setDepartmentId} />
        <ProjectSelect value={projectId} onChange={setProjectId} />
      </div>

      {/* Same rule as the create form: the policy only matters with more
          than one assignee. Lead-only needs a lead - the API refuses it
          otherwise, with a message naming the fix. */}
      {task.assignees.length > 1 && (
        <div>
          <label htmlFor="edit-policy" className="block text-sm font-medium text-slate-700">
            Completes when
          </label>
          <select
            id="edit-policy"
            value={policy}
            onChange={(e) => setPolicy(e.target.value as CompletionPolicy)}
            className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          >
            <option value="ANY">Anyone finishes</option>
            <option value="ALL">Everyone finishes</option>
            <option value="LEAD_ONLY">Only the lead</option>
          </select>
          <p className="mt-1 text-xs text-slate-500">{POLICY_HELP[policy]}</p>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
        <button type="button" onClick={onCancel}
          className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100">
          Cancel
        </button>
        <button type="submit" disabled={saving}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}
